import { HStack, VStack } from '@chakra-ui/react'
import { NumberVerificationBody } from '@missionlabs/api'
import {
    Button,
    Dropdown,
    FormProvider,
    Input,
    useFormatToNumberE164,
    useToast,
    useYupForm,
    ValidatedInput,
    yup
} from '@missionlabs/react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from 'shared/hooks/useAuthenticatedUser'
import { Features, useHasClientFeature } from 'shared/hooks/useHasClientFeature'
import { useVerifyNumberMutation } from 'shared/store'
import { isPhoneLinePlus } from 'shared/utils/env'

interface VerifyNumberFormProps {
    onClose: () => void
    defaultValues: Partial<NumberVerificationBody>
    setFormState: (data: any) => void
}

const VerifyNumberForm: FC<VerifyNumberFormProps> = ({ onClose, defaultValues, setFormState }) => {
    const { t } = useTranslation()

    const { data: user } = useAuthenticatedUser()

    const { toast } = useToast()
    const isMobileNumberClientEnabled = useHasClientFeature(Features.mobile_numbers)

    const userID = user?.ID
    const clientID = user?.clientID

    const { isUKMobileNumber, isValidPhoneNumber } = useFormatToNumberE164()
    const [postVerifyNumber, { isLoading }] = useVerifyNumberMutation()

    const verifySchema = yup.object().shape(
        {
            userID: yup.string().required(),
            clientID: yup.string().required(),
            verifyNumber: yup
                .string()
                .test({
                    message: {
                        key: 'validations.invalidNumber'
                    },
                    test: value => isValidPhoneNumber(value)
                })
                .required(),
            verifyMethod: yup.string().when('verifyNumber', {
                is: value => isUKMobileNumber(value),
                then: () => yup.string().oneOf(['call', 'sms']).required(),
                otherwise: () => yup.string().oneOf(['call']).required()
            })
        },
        [['verifyMethod', 'verifyNumber']]
    )

    const methods = useYupForm<NumberVerificationBody>(verifySchema, {
        defaultValues
    })

    const {
        watch,
        setValue,
        formState: { isValid }
    } = methods

    const { verifyMethod, verifyNumber } = watch()

    const isMobile = useMemo(() => {
        return isUKMobileNumber(verifyNumber)
    }, [verifyNumber, isUKMobileNumber])

    useEffect(
        function unsetSmsVerifyMethodIfNotMobile() {
            if (verifyMethod === 'sms' && !isMobile) {
                setValue('verifyMethod', 'call')
            }
        },
        [isMobile, verifyMethod, setValue]
    )

    useEffect(
        function setUserAndClientIDs() {
            if (userID) setValue('userID', userID)
            if (clientID) setValue('clientID', clientID)
        },
        [userID, clientID, setValue]
    )

    const verificationMethodOptions = useMemo(() => {
        if (isMobile) {
            return [
                { label: t('Call'), value: 'call' },
                { label: t('SMS'), value: 'sms' }
            ]
        }
        return [{ label: 'Call', value: 'call' }]
    }, [isMobile, t])

    const onSubmitNumber = useCallback(
        async (data: NumberVerificationBody) => {
            if (isMobile && !isMobileNumberClientEnabled) {
                toast({
                    title: 'Mobile numbers are not supported on your current plan',
                    status: 'error'
                })
                return
            }

            const { ID = '' } = await postVerifyNumber(data).unwrap()
            setFormState(prev => ({ ...prev, ...data, ID }))
        },
        [isMobile, isMobileNumberClientEnabled, postVerifyNumber, setFormState, toast]
    )

    const mirroringLabel = isPhoneLinePlus
        ? 'admin.numbers.mirror.enterNumberPhoneLinePlus'
        : 'admin.numbers.mirror.enterNumber'

    return (
        <FormProvider {...methods}>
            <VStack as="form" onSubmit={methods.handleSubmit(onSubmitNumber)} h="full" p={4}>
                <HStack w="full" align="flex-start" justify="space-evenly" h={'90%'}>
                    <ValidatedInput name="verifyNumber" label={t(mirroringLabel)}>
                        <Input autoComplete="off" />
                    </ValidatedInput>

                    <ValidatedInput name="verifyMethod" label={t('admin.numbers.mirror.sendBy')}>
                        <Dropdown
                            width="full"
                            options={verificationMethodOptions}
                            value={watch('verifyMethod')}
                            onChange={value =>
                                setValue('verifyMethod', value, {
                                    shouldDirty: true,
                                    shouldTouch: true
                                })
                            }
                        />
                    </ValidatedInput>
                </HStack>

                <HStack h={'10%'} w="full">
                    <Button w="full" size="lg" variant="secondary" onClick={onClose}>
                        {t('Cancel')}
                    </Button>
                    <Button
                        w="full"
                        size="lg"
                        variant="creationary"
                        isLoading={isLoading}
                        isDisabled={!isValid}
                        type="submit"
                    >
                        {t('Submit')}
                    </Button>
                </HStack>
            </VStack>
        </FormProvider>
    )
}

export default VerifyNumberForm
